/**
 * The anonymous trial counter, shared by the landing page and the tool.
 *
 * One 30-day window matching the server's anonMonthlyLimiter. The trial is a taste, not a
 * habit: it stays far below the free account's monthly quota so signing up is always an upgrade.
 *
 * This is a UI counter only. The server is the authority and answers 429/ANON_LIMIT_REACHED.
 */

import { getPricingSync } from './pricing.js';

const KEYS = {
  count: 'anon_month_count',
  start: 'anon_month_start'
};

const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

function read(key) {
  try { return parseInt(localStorage.getItem(key) || '0', 10) || 0; } catch (e) { return 0; }
}

function write(key, value) {
  try { localStorage.setItem(key, String(value)); } catch (e) {}
}

function windowState() {
  const now = Date.now();
  const start = read(KEYS.start);
  if (!start || now - start >= MONTH_MS) {
    write(KEYS.start, now);
    write(KEYS.count, 0);
    return { count: 0, start: now };
  }
  return { count: read(KEYS.count), start };
}

/** Remaining tries, plus when the 30-day window rolls over. */
export function getAnonTrialState() {
  const { per30Days } = getPricingSync().anonymous;
  const { count, start } = windowState();
  const remaining = Math.max(0, per30Days - count);
  return {
    remaining,
    per30Days,
    resetsAt: start + MONTH_MS,
    exhausted: remaining === 0
  };
}

export function getAnonRemaining() {
  return getAnonTrialState().remaining;
}

/** Count one successful enhancement. Returns the new remaining count. */
export function recordAnonEnhancement() {
  const { count } = windowState();
  write(KEYS.count, count + 1);
  return getAnonRemaining();
}

/** The server said no. Burn the window so the UI stops promising tries it cannot deliver. */
export function exhaustAnonTrial() {
  windowState();
  write(KEYS.count, getPricingSync().anonymous.per30Days);
}

/** "resets on 4 November". */
export function formatTrialReset(resetsAt) {
  if (resetsAt - Date.now() <= 0) return 'resets now';
  const date = new Date(resetsAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  return `resets on ${date}`;
}
